import { inject, Injectable } from '@angular/core';
import { MSV } from '@enums/MSV.enum';
import { GroupToolDataDTO } from '@interfaces/KT/GroupToolData.interface';

import { EnvironmentService } from './environment.service';
import { getCurrentCulture } from '@utils/Navigation/getCurrentCulture.utils';
import { Market } from '@type/Generic/Market.type';
import { SessionStorageKey } from '@enums/SessionStorageKey.enum';
import { MvpService, MvpVariant } from './mvp.service';

@Injectable({
  providedIn: 'root',
})
export class MarketService {
  protected get marketCode() {
    return window.marketCode as Market;
  }
  private get cckBasePath() {
    return window.cckBasePath;
  }

  public readonly IT = 'it-IT';

  protected _environmentService = inject(EnvironmentService);
  private readonly _mvpService = inject(MvpService);

  private readonly lookupVariable = this._environmentService.isProd() ? 'cckMsvPRO' : 'cckMsvPRE';

  public hasMyCostaBannerEnabled = () => true;
  public hasFiscalCodeEnabled = this.containsMarketWithAgency(MSV.FISCOD_ENABLED);
  public hasRecommendedEnabled = this.containsMarketWithAgency(MSV.RECOMMENDED);
  public hasGiftCardEnabled = this.containsMarketWithAgency(MSV.GIFTCARD);
  public hasCalendarioEventiEnabled = this.containsMarketWithAgency(MSV.EVENTS_CALENDAR);
  public hasWebCheckinBlockEnabled = this.containsMarketWithAgency(MSV.ENABLED_WEB_CHECK_IN);
  public isPreventivoEmozDisabledMarket = this.containsMarketWithAgency(MSV.EMOTIONAL_QUOTATION);
  public isNewRoomSelectorEnabledMarket = this.containsMarketWithAgency(MSV.NEW_ROOM_SELECTOR);
  public isRoomSelectorTwoEnabledMarket = this.containsMarketWithAgency(MSV.NEW_ROOM_SELECTOR_TWO);
  public isCostaClub2022EnabledMarkets = this.containsMarketWithAgency(MSV.CCLUB_2022);
  public hasNetiEnabled = this.containsMarketWithAgency(MSV.NETI);
  public hasLanguageSwitch = this.containsCulture(MSV.LANGUAGE_SWITCH);
  public hasNewDocumentSection = this.containsMarketWithAgency(MSV.NEW_DOCUMENT_SECTION);
  public hasCClubToggleEnabled = this.containsMarketWithAgency(MSV.CCLUB_TOGGLE);
  public hasAdditionalInfoEnabled = this.containsMarketWithAgency(MSV.ADDITIONAL_INFO);
  public hasDisableDepositDisabledMarkets = this.containsMarketWithAgency(MSV.DISABLE_DEPOSIT);
  public isEnabledCostaClubNoDefaultMarkets = this.containsMarketWithAgency(MSV.CCLUB_DEFAULT_NO);
  public isEnabledPaxNumDefaultMarkets = this.containsMarketWithAgency(MSV.PAX_NUM_DEFAULT);
  public isEnabledPaxAgesDefaultMarkets = this.containsMarketWithAgency(MSV.PAX_AGE_DEFAULT);
  public isEnabledTransportationInfoMarkets = this.containsMarketWithAgency(MSV.TRANSPORTATION_INFO);
  public isEnabledTransportationSelectionMarkets = this.containsMarketWithAgency(MSV.TRANSPORTATION_SELECTION);
  public isEnabledNationalitySelectionMarkets = this.containsMarketWithAgency(MSV.NATIONALITY_SELECTION);
  public isNewBookingSearchEnabled = this.containsMarketWithAgency(MSV.MYBOOKING_SEARCH);
  public isLoginCarouselEnabled = this.containsMarketWithAgency(MSV.LOGIN_CAROUSEL);
  public isNewSummaryPrintEnabled = this.containsMarketWithAgency(MSV.NEW_SUMMARY_PRINT);
  public isNewSummaryPrintEditingEnabled = this.containsMarketWithAgency(MSV.NEW_SUMMARY_PRINT_EDIT);
  public isAgencyDetailsExtendedEnabled = this.containsMarketWithAgency(MSV.AGENCY_DETAIL_EXTENDED);
  public hasGroupPaymentEnabled = this.containsMarketWithAgencyOrNetwork(MSV.GROUP_PAYMENT);
  public hasSeaDestination = this.containsMarketWithAgency(MSV.SEA_DESTINATION);
  public hasBookingPartialPaymentEnabled = this.containsMarketWithAgency(MSV.BOOKING_PARTIAL_PAYMENT);
  public hasUpsellSsvBanner = this.containsMarketWithAgency(MSV.UPSELL_SSV);
  public hasNewMyExplorLayout = this.containsMarketWithAgencyAndABTest(MSV.NEW_MYEXPLOR_LAYOUT);
  public hasWorldPayRedirect = this.containsMarketWithAgency(MSV.NEW_WORLD_PAY_REDIRECT);
  public hasGroupDepartedEnabled = this.containsMarketWithAgency(MSV.DEPARTED_GROUP);
  public hasHideGroupRateEnabled = this.containsMarketWithAgency(MSV.HIDE_GROUP_RATE);
  public hasCaliforniaLawEnabled = this.containsMarketWithAgency(MSV.CALIFORNIA_LAW);
  public hasUpsellFare = this.containsMarketWithAgencyAndABTest(MSV.UPSELL_FARE);
  public hasLoginConfirmAlert = this.containsMarketWithAgency(MSV.LOGIN_CONFIRM_ALERT);
  public hasFullPaymentsReceivedOverride = this.containsMarketWithAgency(MSV.FULLPAYMENTSRECEIVED_OVERRIDE);
  public hasNewUserPages = this.containsMarketWithAgency(MSV.NEW_USER_PAGE);
  public hasCheckUserEmailEnabled = this.containsMarketWithAgency(MSV.CHECK_USER_EMAIL);
  public hasPartialPaymentNetAmount = this.containsMarketWithAgency(MSV.PARTIAL_PAYMENT_NET);
  public hasCustomPromoEnabled = this.containsMarketWithAgency(MSV.CUSTOM_PROMO);
  public hasNewGeolocalizationService = this.containsMarketWithAgencyOrNetwork(MSV.GEOLOC_SERVICE);
  public hasShowPriceDeltaCategorySelection = this.containsMarketWithAgencyOrNetwork(MSV.PRICE_DELTA_CATEGORY_SELECTION);
  public hasNewCategorySelectionUx = this.containsMarketWithAgencyOrNetwork(MSV.NEW_CATEGORY_SELECTION_UX);
  public hasChangeAgencyAdmin = this.containsMarketWithAgencyOrNetwork(MSV.CHANGE_AGENCY_ADMIN);
  public hasBusStationCityOrderEnabled = this.containsMarketWithAgencyOrNetwork(MSV.BUS_STATION_CITY_ORDER);
  public hasRollbedAlertEnabled = this.containsMarketWithAgencyOrNetwork(MSV.ROLLBED_ALERT);
  public hasLoginErrorWarningEnabled = this.containsMarketWithAgencyOrNetwork(MSV.LOGIN_ERROR_WARNING);
  public hasNewFamilyPlan = this.containsMarketWithAgencyOrNetwork(MSV.NEW_FAMILY_PLAN);
  public hasNewBookingQuoteEditEnabled = this.containsMarketWithAgencyOrNetwork(MSV.NEW_BOOKING_QUOTE_EDIT);
  public hasViewCommissionFlag = this.containsMarketWithAgencyOrNetwork(MSV.VIEW_COMMISSION_FLAG);
  public hasButtonNewUIEnabled = this.containsTimedWithLocalFallback(MSV.NEW_BOOKING_QUOTE_EDIT);
  public hasGrqFlightsEnabled = this.containsMarketWithAgencyOrNetwork(MSV.GRQ_FLIGHTS);
  public hasNewTransportationUIEnabled = this.containsMarketWithAgencyOrNetwork(MSV.NEW_TRANSPORTATION_UI);
  public hasSearchByGatewayEnabled = this.containsMarketWithAgencyOrNetwork(MSV.GATEWAY_SEARCH);
  public hasSegmentFilterEnabled = this.containsMarketWithAgencyOrNetwork(MSV.SEGMENT_FILTER);
  public hasPaymentPanelUserFlag = this.containsMarketWithAgencyOrNetwork(MSV.PAYMENT_PANEL_FLAG);
  public hasShowServiceChargesIncluded = this.containsMarketWithAgencyOrNetwork(MSV.HSC_INCLUDED);
  public hasCClubDiscountEnabled = this.containsMarketWithAgencyOrNetwork(MSV.CCLUB_DISCOUNT);
  public hasFreeFastTrackEnabled = this.containsMarketWithAgencyOrNetwork(MSV.FREE_FAST_TRACK);
  public hasNewSsvCategorizationEnabled = this.containsMarketWithAgencyOrNetwork(MSV.NEW_SSV);
  public hasAddUserContestEnabled =  this.containsMarketWithAgencyOrNetwork(MSV.ADD_USER_CONTEST);
  public hasPortPriorityEnabled = this.containsMarketWithAgencyOrNetwork(MSV.PORT_PRIORITY);
  public hasRemoveGatewayCodeEnabled = this.containsMarketWithAgencyOrNetwork(MSV.REMOVE_GTW_CODE);
  public hasTrainEquipmentEnabled = this.containsMarketWithAgencyOrNetwork(MSV.TRAIN_EQUIPMENT);
  public hasPrintSignaturePageEnabled = this.containsMarketWithAgencyOrNetwork(MSV.PRINT_SIGNATURE_PAGE);
  public hasPromoBlackFridayEnabled = this.containsMarketWithAgencyOrNetwork(MSV.PROMO_BLACK_FRIDAY);
  public hasNewFunnel26Enabled = this.containsMarketWithAgencyOrNetwork(MSV.NEW_FUNNEL_26);
  public hasAlternativeWeeksEnabled = this.containsMarketWithAgencyOrNetwork(MSV.ALTERNATIVE_WEEKS);
  public hasMultipleGroupFilter = this.containsMarketWithAgencyOrNetwork(MSV.MULTIPLE_GROUP_FILTERS);
  public hasCommissionVulki = this.containsMarketWithAgencyOrNetwork(MSV.COMMISSION_VULKI);
  public hasTravelAgencyProfileEnabled = this.containsMarketWithAgencyOrNetwork(MSV.TRAVEL_AGENCY_PROFILE);
  public hasSubAgentsEnabled = this.containsMarketWithAgencyOrNetwork(MSV.SUBAGENTS);
  public isEnabledProgressBar = (agency: string = null) => this.containsMarketWithAgencyOrNetwork(MSV.PROGRESS_BAR)(agency) && !this.hasNewFunnel26Enabled(agency);
  public hasUpsellModalEnabled = this.containsMarketWithAgencyOrNetwork(MSV.UPSELL_MODAL);
  public hasItineraryConceptEnabled = this.containsMarketWithAgencyOrNetwork(MSV.ITINERARY_CONCEPT);
  public hasSuperCommissionEnabled = this.containsMarketWithAgencyOrNetwork(MSV.SUPERCOMM_FLAG);
  public hasHiddenPaymentButtonEnabled = this.containsMarketWithAgencyOrNetwork(MSV.HIDE_PAYMENT_BUTTON);
  public hasDeutscheBahnVoucherEnabled = this.containsMarketWithAgencyOrNetwork(MSV.DEUTSCHE_BAHN_VOUCHER);
  public hasRailProjectEnabled = this.containsMarketWithAgencyOrNetwork(MSV.RAIL_PROJECT);
  public hasWithholdingTaxEnabled = this.containsMarketWithAgencyOrNetwork(MSV.WITHHOLDING_TAX);
  public hasPriceStatsEnabled = this.containsMarketWithAgencyOrNetwork(MSV.PRICE_STATS);
  public hasShowReaNumberEnabled = this.containsMarketWithAgencyOrNetwork(MSV.SHOW_REA_NUMBER);
  public hasShowSHCIconEnabled = this.containsMarketWithAgencyOrNetwork(MSV.SHOW_HSC_INCLUDED_ICON);
  public hasCartUXEnabled = this.containsMarketWithAgencyOrNetwork(MSV.CART_UX);
  public hasCartFaresUXEnabled = this.containsMarketWithAgencyOrNetwork(MSV.CART_FARES_UX);
  public getResultCardMvpVariant = (): MvpVariant => {
    if (this._mvpService.isBetaTestingEnabled() && this._mvpService.isBetaTestingAccepted()) {
      return this._mvpService.getBetaResultCardVariant();
    }

    if (this.hasCartFaresUXEnabled()) return 'carrello-w-fares';
    return this.hasCartUXEnabled() ? 'carrello' : 'default';
  };

  public hasNewFareSelectionEnabled = this.containsMarketWithAgencyOrNetwork(MSV.FARE_SELECTION_NEW);

  public refreshUI() {
    this.hasButtonNewUIEnabled = this.containsLocalOverrideOrMarketWithAgencyOrNetwork(MSV.NEW_BOOKING_QUOTE_EDIT, false);
  }

  private getMsvValue(key: MSV) {
    return window[this.lookupVariable][key];
  }

  private containsCulture(key: MSV): () => boolean {
    return () => {
      const data = this.getMsvValue(key);
      const culture = getCurrentCulture();
      return data?.hasOwnProperty(culture);
    };
  }

  protected getNetworkCode(): string {
    let agencyData;
    try {
      agencyData = JSON.parse(sessionStorage.getItem(SessionStorageKey.AGENCY_DATA));
    } catch(e) {
      return "";
    }
    return agencyData?.Network?.trim().toUpperCase() || "";
  }

  private containsTimedWithLocalFallback(key: MSV):  (agency?: string) => boolean {
    const now = new Date();
    const data = this.getMsvValue(key);

    if (!data) return (agency: string = null) => false;

    const timed = data.__timed;
    if (!timed || !timed.endDate || !timed.lsStrategy) {
      console.warn(`MSV ${key} missing timed config`);
      return (agency: string = null) => false;
    }

    const endDate = new Date(timed.endDate);
    const lsValue = localStorage.getItem(key);

    if (now < endDate) {
      switch (timed.lsStrategy) {
        case 'priority':
          if (lsValue !== null) return (agency: string = null) => lsValue === 'true';
          return this.containsLocalOverrideOrMarketWithAgencyOrNetwork(key, true);
        case 'fallback':
          return this.containsLocalOverrideOrMarketWithAgencyOrNetwork(key, false);
        case 'ignore':
          return this.containsMarketWithAgencyOrNetwork(key);
        default:
          console.warn(`Unknown lsStrategy ${timed.lsStrategy} for ${key}`);
          return (agency: string = null) => false;
      }
    }

    return this.containsMarketWithAgencyOrNetwork(key);
  }


  private containsLocalOverrideOrMarketWithAgencyOrNetwork(key: MSV, getMsvValue = true): (agency?: string) => boolean {
    const data = localStorage.getItem(key);

    //Se non è presente il valore in local storage allora prendo quello dell'MSV
    if (data) {
      return (agency: string = null) => data === 'true';
    } else if(getMsvValue){
      return this.containsMarketWithAgencyOrNetwork(key);
    } else return (agency: string = null) => false;
  }

  private containsMarketWithAgencyOrNetwork(key: MSV): (agency?: string) => boolean {
    return (agency: string = null) => {
      agency ||= window.agencyCode;
      const network = this.getNetworkCode();

      const data = this.getMsvValue(key)
      if (!data) {
        console.warn("MSV: Can't get "+key);
        return false;
      }

      if (data.hasOwnProperty(this.marketCode)) {
        const marketConfig = data[this.marketCode];
        return marketConfig && (marketConfig.indexOf('*') >= 0 || marketConfig.indexOf(agency) >= 0 || marketConfig.indexOf(network) >= 0);
      } else if (
        (typeof data === 'object' && data.hasOwnProperty("*")) ||
        ( Array.isArray(data) && data.indexOf("*") >= 0)
      ) {
        return true;
      }
      return false;

    };
  }

  private containsMarketWithAgency(key: MSV): (agency?: string) => boolean {
    return (agency: string = null) => {
      agency ||= window.agencyCode;

      const data = this.getMsvValue(key)
      if (!data) {
        console.warn("MSV: Can't get "+key);
        return false;
      }

      if (data.hasOwnProperty(this.marketCode)) {
        const marketConfig = data[this.marketCode];
        return marketConfig && (marketConfig.indexOf('*') >= 0 || marketConfig.indexOf(agency) >= 0);
      } else if (
        (typeof data === 'object' && data.hasOwnProperty("*")) ||
        ( Array.isArray(data) && data.indexOf("*") >= 0)
      ) {
        return true;
      }
      return false;

    };
  }

  private containsMarketWithAgencyAndABTest(key: MSV): (agency?: string) => boolean {
    return (agency: string = null) => {
      agency ||= window.agencyCode;

      const data = this.getMsvValue(key)
      if (!data) {
        console.warn("MSV: Can't get "+key);
        return false;
      }

      const hasOptedOutABTest = localStorage.getItem(`AB_TEST_${key}`) === 'false';
      if (data.hasOwnProperty(this.marketCode)) {
        const marketConfig = data[this.marketCode];
        return marketConfig && (marketConfig.indexOf('*') >= 0 || (marketConfig.indexOf(agency) >= 0 && !hasOptedOutABTest));
      } else if (
        (typeof data === 'object' && data.hasOwnProperty("*")) ||
        ( Array.isArray(data) && data.indexOf("*") >= 0)
      ) {
        return true;
      }
      return false;

    };
  }

  public assignPricingIcon(imageUrl: string) {
    if (imageUrl?.indexOf('_new2021.') < 0) {
      const [imageName, imageExt] = imageUrl.split('.');
      return imageName + '_new2021.' + imageExt;
    }
    return imageUrl;
  }

  public getAgencyData(): GroupToolDataDTO {
    try {
      return JSON.parse(sessionStorage.getItem(SessionStorageKey.AGENCY_DATA));
    } catch (e) {
      console.warn('agency data corrupted', e);
      return {};
    }
  }

  public getAgencyMarketCode(): string | null {
    try {
      const data = JSON.parse(sessionStorage.getItem(SessionStorageKey.TRAVEL_AGENCY_PROFILE));
      return data?.Agency?.Market || null;
    } catch {
      return null;
    }
  }

  public getFareHidingRules(): {
    [step: string]: { whenPresent?: string[]; hide?: string[]; disable?: string[] }[];
  } {
    const data = this.getMsvValue(MSV.FARE_HIDING_RULES) as {
      m: string;
      hide?: string[];
      disable?: string[];
      whenPresent?: string[];
      s?: string[];
      e?: string[];
    }[];

    const rulesByStep: {
      [step: string]: { whenPresent?: string[]; hide?: string[]; disable?: string[] }[];
    } = {};
    const agencyMarket = this.getAgencyMarketCode();

    data?.forEach((config) => {
      const isCorrectMarket = config.m === this.marketCode || config.m === agencyMarket;
      const isAllMarkets = config.m === '*';
      const isMarketExcluded = (config.e || []).indexOf(this.marketCode) >= 0
        || (agencyMarket && (config.e || []).indexOf(agencyMarket) >= 0);

      if (isCorrectMarket || (isAllMarkets && !isMarketExcluded)) {
        const rule: { whenPresent?: string[]; hide?: string[]; disable?: string[] } = {};
        if (config.whenPresent?.length) rule.whenPresent = config.whenPresent;
        if (config.hide?.length) rule.hide = config.hide;
        if (config.disable?.length) rule.disable = config.disable;

        (config?.s || []).forEach((step) => {
          if (!rulesByStep[step]) rulesByStep[step] = [];
          rulesByStep[step].push(rule);
        });
      }
    });

    return rulesByStep;
  }

  public getWebCheckInAgencyBlockTooCloseDays(): number {
    const data = this.getMsvValue(MSV.DISABLED_WEB_CHECK_IN) as {
      m: string; // MARKET
      d: number; // DAYS
      e: string[]; // EXCLUDED MARKETS
    }[];
    let days = -1;

    data?.forEach((config) => {
      const isCorrectMarket = config.m === this.marketCode;
      const isAllMarkets = config.m === '*';
      const isMarketExcluded = (config.e || []).indexOf(this.marketCode) >= 0;

      if (isCorrectMarket || (isAllMarkets && !isMarketExcluded)) {
        if (config.d >= 0) days = config.d;
      }
    });

    return days;
  }

  public hasFinancingEnabled() {
    const isEnabled = this.containsMarketWithAgency(MSV.FINANCING);
    const agencydata = this.getAgencyData();
    const superConsortium = agencydata?.SuperConsortium || '';
    const toExclude = superConsortium.indexOf('I-OLT') >= 0;

    return isEnabled && !toExclude;
  }

  public hasShipAndShorex() {
    const data = this.getMsvValue(MSV.SHIP_AND_SHOREX) as {
      [marketCode: string]: string;
    };

    return data.hasOwnProperty(this.marketCode);
  }

  public getShipAndShorexPreselectedFilter() {
    const data = this.getMsvValue(MSV.SHIP_AND_SHOREX) as {
      [marketCode: string]: string;
    };

    return data[this.marketCode] || undefined;
  }

  public getLanguageSwitchLink(): {
    l: string;
    t: string;
  } {
    const data = this.getMsvValue(MSV.LANGUAGE_SWITCH) as {
      [culture: string]: {
        l: string; // Link new url
        t: string; // Text new url
      };
    };

    return data?.[getCurrentCulture()] || undefined;
  }
}
